import { useCallback, useEffect, useMemo, useState } from "react"
import { IframeDialog } from "@/components/ux/iframe.dialog"

interface BridgeTosMessage {
  signedAgreementId: string;
}

interface BridgeTosIframeProps {
  onOpenChange: (open: boolean) => void;
  onAccept: () => void;
  bridgeTosLink: string;
  open?: boolean;
  onRedirectBack?: () => void;
}

export default function BridgeTosIframe({
  onOpenChange,
  onAccept,
  bridgeTosLink,
  open,
  onRedirectBack,
}: BridgeTosIframeProps) {
  const [isOpen, setIsOpen] = useState(open ?? true)

  useEffect(() => {
    if (open === undefined) {
      return
    }

    setIsOpen(open)
  }, [open])

  const bridgeOrigin = useMemo(() => {
    try {
      return new URL(bridgeTosLink).origin
    } catch (error) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn("[BridgeTosIframe] ⚠️ Unable to parse bridgeTosLink origin", error)
      }
      return null
    }
  }, [bridgeTosLink])

  const handleIframeLoad = useCallback((iframe: HTMLIFrameElement) => {
    try {
      const currentUrl = iframe.contentWindow?.location.href || iframe.src
      
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log(`[BridgeTosIframe] 📄 IFRAME LOAD:`, {
          currentUrl,
          ourDomain: window.location.origin,
          isOurDomain: currentUrl?.startsWith(window.location.origin)
        })
      }

      // If the iframe has navigated to our own domain, it means Bridge redirected back
      if (currentUrl && currentUrl.startsWith(window.location.origin)) {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.log(`[BridgeTosIframe] 🔄 DETECTED REDIRECT BACK TO OUR APP - closing iframe`)
        }
        
        // Close the iframe
        setIsOpen(false)
        onOpenChange(false)
        
        // Notify parent that we redirected back
        onRedirectBack?.()
      }
    } catch (error) {
      // Cross-origin access might fail, which is expected for external domains
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log(`[BridgeTosIframe] ⚠️ Could not access iframe location (expected for cross-origin):`, error)
      }
    }
  }, [onOpenChange, onRedirectBack])

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(`[BridgeTosIframe] 🏗️ COMPONENT MOUNTED/UPDATED:`, {
      isOpen,
      bridgeTosLink: bridgeTosLink ? 'present' : 'missing',
      bridgeTosLinkPreview: bridgeTosLink?.substring(0, 50) + '...'
    })
  }

  useEffect(() => {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(`[BridgeTosIframe] 📡 SETTING UP MESSAGE LISTENER for TOS iframe`)
    }
    
    const handler = (e: MessageEvent) => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log(`[BridgeTosIframe] 📨 MESSAGE RECEIVED:`, {
          origin: e.origin,
          data: e.data,
          source: e.source
        })
      }

      try {
        // Validate origin for security
        const originMatches =
          typeof e.origin === "string" &&
          ((bridgeOrigin !== null && e.origin === bridgeOrigin) ||
            bridgeTosLink.startsWith(e.origin))

        if (originMatches) {
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.log(`[BridgeTosIframe] ✅ ORIGIN VALIDATED: ${e.origin} matches bridgeTosLink`)
          }
          const messageData = e.data as BridgeTosMessage

          if (messageData?.signedAgreementId) {
            if (import.meta.env.DEV) {
              // eslint-disable-next-line no-console
              console.log(`[BridgeTosIframe] 🎉 TOS SIGNED SUCCESSFULLY:`, {
                signedAgreementId: messageData.signedAgreementId,
                fullMessageData: messageData
              })
              // eslint-disable-next-line no-console
              console.log(`[BridgeTosIframe] 🔄 IMMEDIATELY closing iframe and triggering KYC`)
            }
            
            // Immediately close iframe
            const closeIframe = () => {
              setIsOpen(false)
              onOpenChange(false)
            }

            closeIframe()

            // Immediately trigger KYC flow (no delay needed)
            try {
              onAccept()
              if (import.meta.env.DEV) {
                // eslint-disable-next-line no-console
                console.log(`[BridgeTosIframe] ✅ onAccept callback completed - should open KYC link now`)
              }
            } catch (error) {
              if (import.meta.env.DEV) {
                // eslint-disable-next-line no-console
                console.error(`[BridgeTosIframe] ❌ ERROR in onAccept callback:`, error)
              }
              window.location.reload()
            }
          } else {
            // console.log(`[BridgeTosIframe] ⚠️ MESSAGE RECEIVED but no signedAgreementId:`, messageData)
          }
        } else {
          // console.warn(`[BridgeTosIframe] 🚫 IGNORING MESSAGE from unauthorized origin:`, {
          //   receivedOrigin: e.origin,
          //   expectedOrigin: bridgeTosLink ? new URL(bridgeTosLink).origin : 'unknown',
          //   bridgeTosLink: bridgeTosLink?.substring(0, 50) + '...'
          // })
        }
      } catch (error) {
        // console.error(`[BridgeTosIframe] 💥 ERROR processing message:`, {
        //   error,
        //   messageData: e.data,
        //   origin: e.origin
        // })
        // Don't close iframe on error to prevent user getting stuck
        // console.log(`[BridgeTosIframe] 🛡️ PREVENTING IFRAME CLOSE due to error`)
      }
    }

    // console.log(`[BridgeTosIframe] 👂 ADDING message event listener`)
    window.addEventListener("message", handler)
    return () => {
      // console.log(`[BridgeTosIframe] 🧹 CLEANING UP message event listener`)
      window.removeEventListener("message", handler)
    }
  }, [bridgeOrigin, bridgeTosLink, onAccept, onOpenChange])

  const handleOpenChange = (open: boolean) => {
    // console.log(`[BridgeTosIframe] 🔄 handleOpenChange called:`, {
    //   requestedState: open,
    //   currentState: isOpen,
    //   willChange: open !== isOpen
    // })
    setIsOpen(open)
    onOpenChange(open)
  }

  if (!isOpen) {
    return null
  }

  return (
    <IframeDialog
      name="bridge-tos"
      src={bridgeTosLink}
      onOpenChange={handleOpenChange}
      title="Bridge Terms of Service"
      iframeClassName="h-[350px] w-full"
      open={isOpen}
      onIframeLoad={handleIframeLoad}
    />
  )
}
